import { useEffect, useRef } from 'react'

import { round } from 'lodash'

import { TableResizer } from '@eastcoal/common/components/TableResizer'
import { TableResizerChangePayload } from '@eastcoal/common/components/TableResizer/interface'

import { $getNearestNodeFromDOMNode } from '@eastcoal/editor/exports'
import { useEditor } from '@eastcoal/editor/hooks/useEditor'

import { $isTableVariableNode } from '../../nodes/TableVariableNode'
import { TableVariableCellClassName } from '../../ui/TableVariablePlaceholder'

const MIN_COLUMN_WIDTH = 60

const calcColWidths = ({
  cellElement,
  value,
  dragDelta
}: TableResizerChangePayload) => {
  const parentElement = cellElement.parentElement
  if (!parentElement) {
    return
  }
  const nextSiblingElement = cellElement.nextElementSibling
  // 最后一列不允许拖动
  if (!nextSiblingElement) {
    return
  }
  let currentCellNewWidth = value
  const nextSiblingCellRect = nextSiblingElement.getBoundingClientRect()
  let nextSiblingCellNewWidth = nextSiblingCellRect.width - dragDelta
  const minimumWidthThreshold = nextSiblingCellNewWidth - MIN_COLUMN_WIDTH
  if (minimumWidthThreshold < 0) {
    nextSiblingCellNewWidth = MIN_COLUMN_WIDTH
    currentCellNewWidth = currentCellNewWidth - minimumWidthThreshold
  }
  const allSiblingElements = [...parentElement.children]
  const totalWidth = allSiblingElements.reduce((acc, el) => {
    const childRect = el.getBoundingClientRect()
    acc += childRect.width
    return acc
  }, 0)
  return allSiblingElements
    .map(el => {
      if (el === cellElement) {
        return (currentCellNewWidth / totalWidth) * 100
      } else if (el === nextSiblingElement) {
        return (nextSiblingCellNewWidth / totalWidth) * 100
      }
      return (el.getBoundingClientRect().width / totalWidth) * 100
    })
    .map(width => `${round(width * 100) / 100}%`)
}

export const TableVariableCellResizerPlugin = () => {
  const editor = useEditor()
  const rootElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    editor.registerRootListener(rootElement => {
      rootElementRef.current = rootElement
    })
  }, [editor])

  const handleChange = ({
    cellElement,
    ...restPayload
  }: TableResizerChangePayload) => {
    editor.update(() => {
      const node = $getNearestNodeFromDOMNode(cellElement)
      if (!$isTableVariableNode(node)) return
      const colWidths = calcColWidths({ cellElement, ...restPayload })
      if (colWidths) {
        node.setColWidths(colWidths)
      }
    })
  }

  return (
    <TableResizer
      rootElement={rootElementRef}
      isMatchedTarget={cellElement => {
        return cellElement.classList.contains(TableVariableCellClassName)
      }}
      onColWidthChange={handleChange}
    />
  )
}
